/**
 * Framing math for the profile-photo editor (components/PhotoFramer.tsx).
 * Pure, so it is unit-tested; the component does the drawing and input.
 *
 * A frame is the square of the image that becomes the photo:
 *   - (cx, cy) is the image point at the center of the square, in image px;
 *   - zoom >= 1, where 1 = the largest square that fits (side = the image's
 *     shorter edge) and 2 = a square half that size.
 * Every helper returns a clamped frame, so the square never leaves the image.
 */

export type ImageSize = { w: number; h: number };
export type Frame = { cx: number; cy: number; zoom: number };
export type FaceBox = { x: number; y: number; width: number; height: number };

/** Today's photo size limit (services/wasabiUpload.ts), applied to the pick as before. */
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

export const MAX_ZOOM = 4;
/** The saved photo is at most this many px square (never upscaled past the source). */
export const OUTPUT_MAX = 800;
/** A stored photo whose shorter edge is under this is probably already a crop. */
export const SMALL_PHOTO_PX = 400;

/** Side of the framed square, in image px. */
export function frameSide(img: ImageSize, zoom: number): number {
  return Math.min(img.w, img.h) / zoom;
}

export function clampFrame(img: ImageSize, f: Frame): Frame {
  const zoom = Math.min(MAX_ZOOM, Math.max(1, Number.isFinite(f.zoom) ? f.zoom : 1));
  const half = frameSide(img, zoom) / 2;
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Number.isFinite(v) ? v : (lo + hi) / 2));
  return { zoom, cx: clamp(f.cx, half, img.w - half), cy: clamp(f.cy, half, img.h - half) };
}

/**
 * Where the editor opens. Never a plain center crop:
 *   - with a detected face, zoom so the face is about 40% of the square's
 *     width and sit it a little above the center;
 *   - without one, keep the full width and lean to the top: heads usually sit
 *     in the upper third of a portrait photo, so its square starts near the top
 *     (the square's center goes at 38% of the height, clamped). Landscape and
 *     square photos have no vertical room, so they center.
 */
export function defaultFrame(img: ImageSize, face?: FaceBox | null): Frame {
  if (face && face.width > 0 && face.height > 0) {
    const side = Math.max(face.width / 0.4, face.height / 0.45);
    const zoom = Math.min(img.w, img.h) / side;
    const z = Math.min(MAX_ZOOM, Math.max(1, zoom));
    const s = frameSide(img, z);
    return clampFrame(img, { zoom: z, cx: face.x + face.width / 2, cy: face.y + face.height / 2 + s * 0.08 });
  }
  return clampFrame(img, { zoom: 1, cx: img.w / 2, cy: img.h > img.w ? img.h * 0.38 : img.h / 2 });
}

/** Move the photo by a drag of (dx, dy) view px in a view `viewSize` px square. */
export function panBy(img: ImageSize, f: Frame, dx: number, dy: number, viewSize: number): Frame {
  const perView = frameSide(img, f.zoom) / viewSize;
  return clampFrame(img, { ...f, cx: f.cx - dx * perView, cy: f.cy - dy * perView });
}

/**
 * Zoom to `zoom`, keeping the image point under the view point (fx, fy) where
 * it is (pinch midpoint, wheel cursor, or the center for the slider/keys).
 */
export function zoomAt(img: ImageSize, f: Frame, zoom: number, fx: number, fy: number, viewSize: number): Frame {
  const next = Math.min(MAX_ZOOM, Math.max(1, zoom));
  const before = frameSide(img, f.zoom) / viewSize;
  const after = frameSide(img, next) / viewSize;
  const ix = f.cx + (fx - viewSize / 2) * before;
  const iy = f.cy + (fy - viewSize / 2) * before;
  return clampFrame(img, { zoom: next, cx: ix - (fx - viewSize / 2) * after, cy: iy - (fy - viewSize / 2) * after });
}

/**
 * One pinch step: the fingers' midpoint went from `from` to `to` (view px) and
 * their spread changed by `ratio`. Zoom around where the midpoint WAS, then
 * move by the midpoint's travel, so the image point under the fingers stays
 * under them.
 */
export function pinchStep(
  img: ImageSize,
  f: Frame,
  ratio: number,
  from: { x: number; y: number },
  to: { x: number; y: number },
  viewSize: number
): Frame {
  const zoomed = zoomAt(img, f, f.zoom * ratio, from.x, from.y, viewSize);
  return panBy(img, zoomed, to.x - from.x, to.y - from.y, viewSize);
}

/** The source square to export, and the output size (never upscaled, at most OUTPUT_MAX). */
export function cropRect(img: ImageSize, f: Frame): { sx: number; sy: number; side: number; out: number } {
  const c = clampFrame(img, f);
  const side = frameSide(img, c.zoom);
  return {
    sx: c.cx - side / 2,
    sy: c.cy - side / 2,
    side,
    out: Math.max(1, Math.min(OUTPUT_MAX, Math.round(side))),
  };
}

export function isSmallPhoto(img: ImageSize): boolean {
  return Math.min(img.w, img.h) < SMALL_PHOTO_PX;
}
